import React from "react";
import {
  FormConfigField,
  FieldTypeValue,
  FIELD_TYPE,
  FIELD_TYPE_LABELS,
  OPTION_FIELD_TYPES,
  MEDIA_FIELD_TYPES,
  makeFormConfigField,
} from "../shared/types";
import * as S from "../shared/styles";

// ----------------------------  Helpers  ----------------------------

function reorder(list: FormConfigField[]): FormConfigField[] {
  return list.map((item, i) => ({ ...item, display_order: i }));
}

function move(list: FormConfigField[], id: string, dir: number): FormConfigField[] {
  const idx = list.findIndex((f) => f.id === id);
  const swap = idx + dir;
  if (swap < 0 || swap >= list.length) return list;
  const copy = [...list];
  [copy[idx], copy[swap]] = [copy[swap], copy[idx]];
  return reorder(copy);
}

// ----------------------------  FieldBuilder  ----------------------------

interface FieldBuilderProps {
  fields: FormConfigField[];
  onChange: (fields: FormConfigField[]) => void;
}

const FieldBuilder: React.FC<FieldBuilderProps> = ({ fields, onChange }) => {
  const addField = () =>
    onChange([...fields, makeFormConfigField({ display_order: fields.length })]);
  const updateField = (id: string, patch: Partial<FormConfigField>) =>
    onChange(fields.map((f) => (f.id === id ? { ...f, ...patch } : f)));
  const removeField = (id: string) => onChange(reorder(fields.filter((f) => f.id !== id)));
  const moveField = (id: string, dir: number) => onChange(move(fields, id, dir));

  return (
    <div>
      {fields.map((f, idx) => (
        <FieldEditor
          key={f.id}
          field={f}
          index={idx}
          total={fields.length}
          onChange={(patch) => updateField(f.id, patch)}
          onRemove={() => removeField(f.id)}
          onMove={(dir) => moveField(f.id, dir)}
        />
      ))}
      <button style={{ ...S.btnOutline, width: "100%" }} onClick={addField}>
        + Add Field
      </button>
    </div>
  );
};

// ----------------------------  FieldEditor  ----------------------------

interface FieldEditorProps {
  field: FormConfigField;
  index: number;
  total: number;
  onChange: (patch: Partial<FormConfigField>) => void;
  onRemove: () => void;
  onMove: (dir: number) => void;
}

const FieldEditor: React.FC<FieldEditorProps> = ({
  field,
  index,
  total,
  onChange,
  onRemove,
  onMove,
}) => {
  const needsOptions = OPTION_FIELD_TYPES.indexOf(field.type) !== -1;
  const isCheckbox = field.type === FIELD_TYPE.CHECKBOX;
  const isText = field.type === FIELD_TYPE.TEXT_INPUT;
  const isNumeric = field.type === FIELD_TYPE.NUMERIC;
  const isMedia = MEDIA_FIELD_TYPES.indexOf(field.type) !== -1;

  const setOption = (i: number, val: string) =>
    onChange({ options: field.options.map((o, k) => (k === i ? val : o)) });
  const addOption = () => onChange({ options: [...field.options, ""] });
  const removeOption = (i: number) =>
    onChange({ options: field.options.filter((_, k) => k !== i) });

  return (
    <div style={S.fieldCard}>
      <div style={S.fieldCardHeader}>
        <span style={S.fieldCardIndex}>Field {index + 1}</span>
        <div style={S.fieldCardActions}>
          <button
            style={{ ...S.fieldCardActionBtn, opacity: index === 0 ? 0.3 : 1 }}
            disabled={index === 0}
            onClick={() => onMove(-1)}
            title="Move up"
          >
            ↑
          </button>
          <button
            style={{ ...S.fieldCardActionBtn, opacity: index === total - 1 ? 0.3 : 1 }}
            disabled={index === total - 1}
            onClick={() => onMove(1)}
            title="Move down"
          >
            ↓
          </button>
          <button style={S.fieldCardRemoveBtn} onClick={onRemove} title="Remove field">
            ✕
          </button>
        </div>
      </div>

      <div style={S.formRow}>
        <label style={S.formLabel}>Question</label>
        <input
          style={S.formInput}
          value={field.question}
          onChange={(e) => onChange({ question: e.target.value })}
          placeholder="Optional — groups fields under a shared question"
        />
      </div>

      <div style={S.fieldCardGrid}>
        <div style={S.formRow}>
          <label style={S.formLabel}>
            Label<span style={S.formLabelRequired}>*</span>
          </label>
          <input
            style={S.formInput}
            value={field.label}
            onChange={(e) => onChange({ label: e.target.value })}
            placeholder="e.g. Defect Type"
          />
        </div>
        <div style={S.formRow}>
          <label style={S.formLabel}>
            Type<span style={S.formLabelRequired}>*</span>
          </label>
          <select
            style={S.formSelect}
            value={field.type}
            onChange={(e) => {
              const v = e.target.value as FieldTypeValue;
              const keepOptions = OPTION_FIELD_TYPES.indexOf(v) !== -1 || v === FIELD_TYPE.CHECKBOX;
              onChange({
                type: v,
                options: keepOptions ? field.options : [],
                char_limit: null,
                min: null,
                max: null,
                step: null,
              });
            }}
          >
            {Object.values(FIELD_TYPE).map((v) => (
              <option key={v} value={v}>
                {FIELD_TYPE_LABELS[v]}
              </option>
            ))}
          </select>
        </div>
      </div>

      {needsOptions && (
        <div style={S.formRow}>
          <label style={S.formLabel}>Options</label>
          {field.options.map((opt, i) => (
            <div key={i} style={S.optionRow}>
              <input
                style={{ ...S.formInput, flex: 1 }}
                value={opt}
                onChange={(e) => setOption(i, e.target.value)}
                placeholder={"Option " + (i + 1)}
              />
              <button style={S.fieldCardRemoveBtn} onClick={() => removeOption(i)} title="Remove">
                ✕
              </button>
            </div>
          ))}
          <button style={S.addOptionLink} onClick={addOption}>
            + Add option
          </button>
          <div style={{ marginTop: "8px" }}>
            <label style={{ fontSize: "0.875rem", color: "#374151", cursor: "pointer" }}>
              <input
                type="checkbox"
                style={S.formCheckbox}
                checked={field.allow_other}
                onChange={(e) => onChange({ allow_other: e.target.checked })}
              />
              Allow &quot;Other&quot; (free-text fallback)
            </label>
          </div>
        </div>
      )}

      {isCheckbox && (
        <div style={S.formRow}>
          <label style={S.formLabel}>Checkbox label</label>
          <input
            style={S.formInput}
            value={field.options[0] ?? ""}
            onChange={(e) => onChange({ options: [e.target.value] })}
            placeholder="e.g. Verified by auditor"
          />
          <div style={{ fontSize: "0.7rem", color: "#9ca3af", marginTop: "2px" }}>
            Text shown next to the checkbox. The reviewer can check or uncheck it.
          </div>
        </div>
      )}

      {isText && (
        <div style={S.fieldCardGrid}>
          <div style={{ ...S.formRow, display: "flex", alignItems: "center", paddingTop: "20px" }}>
            <label style={{ fontSize: "0.875rem", color: "#374151", cursor: "pointer" }}>
              <input
                type="checkbox"
                style={S.formCheckbox}
                checked={field.multiline}
                onChange={(e) => onChange({ multiline: e.target.checked })}
              />
              Multi-line
            </label>
          </div>
          <div style={S.formRow}>
            <label style={S.formLabel}>Character limit</label>
            <input
              style={S.formInput}
              type="number"
              value={field.char_limit ?? ""}
              onChange={(e) =>
                onChange({ char_limit: e.target.value === "" ? null : Number(e.target.value) })
              }
              placeholder="none"
            />
          </div>
        </div>
      )}

      {isNumeric && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px" }}>
          <div style={S.formRow}>
            <label style={S.formLabel}>Min</label>
            <input
              style={S.formInput}
              type="number"
              value={field.min ?? ""}
              onChange={(e) =>
                onChange({ min: e.target.value === "" ? null : Number(e.target.value) })
              }
              placeholder="none"
            />
          </div>
          <div style={S.formRow}>
            <label style={S.formLabel}>Max</label>
            <input
              style={S.formInput}
              type="number"
              value={field.max ?? ""}
              onChange={(e) =>
                onChange({ max: e.target.value === "" ? null : Number(e.target.value) })
              }
              placeholder="none"
            />
          </div>
          <div style={S.formRow}>
            <label style={S.formLabel}>Step</label>
            <input
              style={S.formInput}
              type="number"
              value={field.step ?? ""}
              onChange={(e) =>
                onChange({ step: e.target.value === "" ? null : Number(e.target.value) })
              }
              placeholder="any"
            />
          </div>
        </div>
      )}

      {!isMedia && (
        <div style={S.formRow}>
          <label style={S.formLabel}>Help Text</label>
          <input
            style={S.formInput}
            value={field.help_text}
            onChange={(e) => onChange({ help_text: e.target.value })}
            placeholder="Optional guidance for auditors"
          />
        </div>
      )}

      <div>
        <label style={{ fontSize: "0.875rem", color: "#374151", cursor: "pointer" }}>
          <input
            type="checkbox"
            style={S.formCheckbox}
            checked={field.mandatory}
            onChange={(e) => onChange({ mandatory: e.target.checked })}
          />
          Mandatory
        </label>
      </div>
    </div>
  );
};

export default FieldBuilder;
